import { Box, Container, Divider } from "@material-ui/core";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  AutoCompleteComponent,
  CurrencyDisplay,
  CustomTable,
  ModalError,
  Page,
  PrimaryButton,
  SearchTextInput,
  StatusChip,
} from "components";
import { ButtonGroupTop } from "components/atoms/Button";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import NumberFormat from "react-number-format";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatPeriodDate, getErrors } from "utils";

function UploadData() {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const isFirstLoad = useRef(true);
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams?.get("search");
  const paramsPage = urlParams?.get("page");
  const paramsSize = urlParams?.get("size");
  const paramsDSP = urlParams?.get("dsp_id");

  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    size: Number(paramsSize) || 10,
    search: paramsSearch || "",
    dsp_id: Number(paramsDSP) || "",
    type: "UD",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [optionDsp, setOptionDsp] = useState([]);
  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };
  const handleUploadDsp = () => {
    const { dsp_id } = queryParams;
    if (dsp_id) {
      history.push({
        pathname: `/admin/add-upload/${dsp_id}`,
        state: {
          selectedDsp: optionDsp?.find(item => item.id === dsp_id) || {},
        },
      });
    } else {
      ModalError("Please select DSP");
    }
  };

  const getOptionDSP = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/dsp/all?process_method=upload`,
        { headers }
      );
      const { data } = res?.data;
      const modifiedData = data?.map(item => ({
        ...item,
        id: item?.dsp_id,
        label: item?.dsp_name,
      }));
      setOptionDsp(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDataTable = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/process/datatable`, {
        headers,
        params: queryParams,
      });

      const { data, meta } = res?.data;
      setDataTable(data);
      const pageCount = getTotalPage(meta?.total, queryParams?.size || 1);
      setTableTotalPage(pageCount);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const debounceDataTable = useCallback(
    debounce(async () => {
      if (queryParams?.dsp_id) {
        setLoadingPage(true);
        await getDataTable();
        setLoadingPage(false);
      }
    }, 500),
    [queryParams]
  );
  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [];
    if (isFirstLoad.current) promises.push(getOptionDSP());
    if (queryParams?.dsp_id) {
      promises.push(getDataTable());
    } else {
      setDataTable([]);
      setTableTotalPage(1);
    }
    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
      isFirstLoad.current = false;
    }
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching && !isFirstLoad.current) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      fetchData();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Upload Data">
      <Container maxWidth={false}>
        <HeaderTitle title="Upload Data" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes?.flatDivider} />
        <Grid container justifyContent="space-between" my="16px">
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "search")
              }
            />
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1} alignItems="center">
              <Grid item>
                <AutoCompleteComponent
                  options={optionDsp}
                  label="Select DSP"
                  value={
                    optionDsp.find(
                      option => option.id === queryParams?.dsp_id
                    ) || null
                  }
                  onChange={value => handleChangeQueryParams(value, "dsp_id")}
                  size="small"
                  width={250}
                />
              </Grid>
              <Grid item>
                <PrimaryButton
                  label="Upload DSP Data"
                  onClick={handleUploadDsp}
                  size="large"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          isHaveAction
          handleView={item => history.push(`/admin/detail-upload/${item?.id}`)}
        />
      </Container>
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/upload",
  },
  {
    label: "Upload Data",
    active: true,
  },
];
const statusTypeMap = {
  Finish: "success",
  Error: "danger",
  Started: "warning",
  Initialize: "warning",
  Running: "warning",
};
const columnTable = [
  {
    name: "id",
    title: "Upload ID",
  },
  {
    name: "header",
    title: "Period",
    renderText: item => formatPeriodDate(item?.start_date, item?.end_date),
  },
  {
    name: "header",
    title: "Total Revenue",
    headerAlign: "right",
    renderText: header => (
      <CurrencyDisplay
        value={header?.revenue || 0}
        decimalScale={2}
        prefix="Rp"
      />
    ),
  },
  {
    name: "record_count",
    title: "Number of Record",
    headerAlign: "right",
    renderText: item => (
      <Box display="flex" justifyContent="flex-end">
        <NumberFormat
          displayType="text"
          value={item}
          thousandSeparator=","
          thousandsGroupStyle="thousand"
        />
      </Box>
    ),
  },
  {
    name: "status",
    title: "Upload Status",
    renderText: status => (
      <StatusChip label={status} type={statusTypeMap[status] || ""} />
    ),
  },
];
export default UploadData;
